import { useCallback, useEffect, useState } from 'react';
import { createSafeResizeObserver } from '@shared/utils/createSafeResizeObserver';
import { useIsomorphicLayoutEffect } from '@shared/utils/hooks/useIsomorphicLayoutEffect';

type ElementSize = Readonly<{ width: number; height: number }>;

const ZERO_SIZE: ElementSize = { width: 0, height: 0 };

/**
 * Client size of a caller-owned ref, kept current on resize. Own-subtree refs are read before first
 * paint (no jump, unlike useSizeObserver); parent refs attach after children's layout effects, so the
 * passive effect picks them up. The element must exist on mount: a later-mounted one is not observed.
 */
export const useElementSize = (ref: React.RefObject<HTMLElement>): ElementSize => {
  const [size, setSize] = useState<ElementSize>(ZERO_SIZE);

  const measure = useCallback((element: HTMLElement) => {
    const { clientWidth: width, clientHeight: height } = element;
    setSize(prev => (prev.width === width && prev.height === height ? prev : { width, height }));
  }, []);

  useIsomorphicLayoutEffect(() => {
    if (ref.current) measure(ref.current);
  }, [ref, measure]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    measure(element);
    const observer = createSafeResizeObserver(() => measure(element), { throttle: false });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, measure]);

  return size;
};
